import { Editor } from "@tinymce/tinymce-react";
import { debounce } from "lodash";
import { Card } from "primereact/card";
import { confirmDialog } from "primereact/confirmdialog";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  generateReferralDraft,
  generateUnderwritingPdf,
} from "../../../api/routes";
import { useToast } from "../../../context-providers/Toast";
import { ThemeContext } from "../../../themes/Theme";
import { downloadBlob } from "../../../util";
import { SafeReportResponse } from "../Conversion";
import { offersReferralSentStatus, readableStatus } from "../dashboard/util";
import {
  useCalculatedPremium,
  useLossRunCommentsForReport,
  useUpdateReport,
} from "../data";
import { buildSnapshotReferralHtml } from "./snapshotTemplate";

type ReferralCardProps = {
  specialtyPropertyInfo: SafeReportResponse;
};

export const ReferralCard: React.FC<ReferralCardProps> = ({
  specialtyPropertyInfo,
}) => {
  // The pricing rows depend on the calculated premium, which loads async.
  const premiumQuery = useCalculatedPremium(
    specialtyPropertyInfo.report_id,
    true
  );
  const premiumDataRef = useRef(premiumQuery.data);
  premiumDataRef.current = premiumQuery.data;

  // Loss-run comments seed a per-year notes row.
  const lossRunCommentsQuery = useLossRunCommentsForReport(
    specialtyPropertyInfo.report_id
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  const lossRunComments = lossRunCommentsQuery.data?.all_comments ?? [];
  // The clear-edits button's TinyMCE setup closure is created once at mount, so
  // it reads the latest comments through a ref rather than the captured value.
  // Mirrors premiumDataRef.
  const lossRunCommentsRef = useRef(lossRunComments);
  lossRunCommentsRef.current = lossRunComments;

  // The draft-email button decides whether to offer the "Referral Sent" status
  // from the report's current status, read through a ref for the same reason.
  const statusRef = useRef(specialtyPropertyInfo.status);
  statusRef.current = specialtyPropertyInfo.status;

  const [text, setText] = useState<string | null>(
    () => specialtyPropertyInfo.report_json.referral_text?.text ?? null
  );

  // When there's no saved referral yet, seed the snapshot once the premium and
  // loss-run comment queries settle so the pricing and notes rows are populated
  // (or cleanly omitted on error). Once `text` is set it is never
  // auto-regenerated, preserving user edits.
  useEffect(() => {
    if (
      text === null &&
      !premiumQuery.isLoading &&
      !lossRunCommentsQuery.isLoading
    ) {
      setText(
        buildSnapshotReferralHtml(
          specialtyPropertyInfo,
          premiumQuery.data,
          lossRunComments
        )
      );
    }
  }, [
    text,
    premiumQuery.isLoading,
    premiumQuery.data,
    specialtyPropertyInfo,
    lossRunCommentsQuery.isLoading,
    lossRunComments,
  ]);

  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";

  const toast = useToast();
  const updateReport = useUpdateReport(specialtyPropertyInfo.report_id);

  // Once a referral draft exists the natural next status is "Referral Sent".
  // Ask rather than set it: the draft still has to be addressed and sent, and
  // the underwriter may be re-referring an account that is further along.
  const offerReferralSentStatus = () => {
    if (!offersReferralSentStatus(statusRef.current)) {
      return;
    }
    const label = readableStatus("referral_sent");
    confirmDialog({
      header: `Mark as ${label}?`,
      message: `The referral draft is ready to send. Update this submission's status to "${label}"?`,
      icon: "pi pi-envelope",
      acceptLabel: "Update status",
      rejectLabel: "Not now",
      accept: async () => {
        try {
          await updateReport.mutateAsync({
            status: { new_status: "referral_sent" },
          });
          toast.show({
            severity: "success",
            summary: "Status updated",
            detail: `This submission is now "${label}".`,
            life: 5000,
          });
        } catch {
          toast.show({
            severity: "error",
            summary: "Could not update status",
            detail: "Use the status menu to mark the referral as sent.",
            life: 5000,
          });
        }
      },
    });
  };

  const [saveState, setSaveState] = useState<{
    dirty: boolean;
    lastSaveTime: number | undefined;
  }>({ dirty: false, lastSaveTime: undefined });

  const saveIcon = (
    <span
      className={
        updateReport.isPending
          ? "pi pi-spin pi-spinner"
          : saveState.dirty
            ? "pi pi-file-edit"
            : "pi pi-check"
      }
      style={{ fontSize: "0.75rem" }}
    />
  );
  const saveMessage = updateReport.isPending
    ? "Saving..."
    : saveState.dirty
      ? "Unsaved changes"
      : saveState.lastSaveTime
        ? "Saved at " + new Date(saveState.lastSaveTime).toLocaleTimeString()
        : "Saved (No changes)";

  const saveStatus = (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: "4px",
        fontSize: "12px",
      }}
    >
      {saveIcon} {saveMessage}
    </span>
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps -- pre-existing, not yet audited
  const debouncedUpdate = useCallback(
    debounce((content) => {
      updateReport.mutateAsync(
        { referralText: { text: content } },
        {
          onSuccess: () =>
            setSaveState({ dirty: false, lastSaveTime: Date.now() }),
        }
      );
    }, 1000),
    []
  );

  return (
    <Card title="Referral" subTitle={saveStatus}>
      {text === null ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "16px",
          }}
        >
          <span className="pi pi-spin pi-spinner" /> Preparing referral…
        </div>
      ) : (
        <Editor
          value={text}
          init={{
            ...{
              height: 500,
              menubar: false,
              plugins: [
                "advlist",
                "autolink",
                "lists",
                "link",
                "charmap",
                "table",
                "help",
                "wordcount",
              ],
              toolbar:
                "bold italic underline | table | numlist bullist | customcopy customdownload customemail clearedits",
              setup: (editor) => {
                // TinyMCE's built-in icon pack has no envelope, so register
                // one (Material Design "email" glyph).
                editor.ui.registry.addIcon(
                  "envelope",
                  '<svg width="24" height="24" viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>'
                );
                editor.ui.registry.addButton("customemail", {
                  icon: "envelope",
                  tooltip: "Create Draft Email",
                  onAction: async () => {
                    const html = editor.getContent({ format: "html" });
                    toast.show({
                      severity: "info",
                      summary: "Creating draft email",
                      detail: "Copying the referral into an Outlook draft...",
                      icon: "pi pi-spin pi-spinner",
                    });
                    try {
                      const response = await generateReferralDraft(
                        specialtyPropertyInfo.report_id,
                        html
                      );
                      window.open(response.draft_url, "_blank");
                      toast.replace({
                        severity: "success",
                        summary: "Draft created",
                        detail: "The draft email opened in a new tab.",
                        life: 5000,
                      });
                      offerReferralSentStatus();
                    } catch {
                      toast.replace({
                        severity: "error",
                        summary: "Error Generating Draft Email",
                        detail:
                          "Something failed while generating the draft email.",
                        life: 5000,
                      });
                    }
                  },
                });
                editor.ui.registry.addButton("clearedits", {
                  icon: "reload",
                  tooltip: "Clear all Edits",
                  onAction: async () => {
                    updateReport.mutateAsync(
                      {
                        referralText: { text: null },
                      },
                      {
                        onSuccess: () =>
                          setSaveState({
                            dirty: false,
                            lastSaveTime: Date.now(),
                          }),
                      }
                    );

                    setText(
                      buildSnapshotReferralHtml(
                        specialtyPropertyInfo,
                        premiumDataRef.current,
                        lossRunCommentsRef.current
                      )
                    );
                  },
                });
                editor.ui.registry.addButton("customdownload", {
                  icon: "export-pdf",
                  tooltip: "Download as PDF",
                  onAction: async () => {
                    const html = editor.getContent({ format: "html" });
                    toast.show({
                      severity: "info",
                      summary: "Generating report",
                      detail: "Adding screenshots...",
                      icon: "pi pi-spin pi-spinner",
                    });
                    try {
                      const blob = await generateUnderwritingPdf(
                        specialtyPropertyInfo.report_id,
                        html
                      );
                      downloadBlob(
                        specialtyPropertyInfo.report_json.company_info
                          .company_name,
                        blob
                      );
                      toast.replace({
                        severity: "success",
                        summary: "Report downloaded",
                        detail: "You can find the report in your downloads.",
                        life: 5000,
                      });
                    } catch {
                      toast.replace({
                        severity: "error",
                        summary: "Something went wrong",
                        life: 5000,
                      });
                    }
                  },
                });
                editor.ui.registry.addButton("customcopy", {
                  icon: "copy",
                  tooltip: "Copy content",
                  onAction: async () => {
                    const content = editor.getContent({ format: "html" });
                    const textContent = editor.getContent({ format: "text" });
                    const htmlBlob = new Blob([content], { type: "text/html" });
                    const plainBlob = new Blob([textContent], {
                      type: "text/plain",
                    });

                    try {
                      await navigator.clipboard.write([
                        new ClipboardItem({
                          "text/html": htmlBlob,
                          "text/plain": plainBlob,
                        }),
                      ]);
                    } catch (err) {
                      // Fallback to text-only if clipboard.write not supported
                      navigator.clipboard.writeText(content);
                    }

                    toast.show({
                      severity: "success",
                      summary: "Copied",
                      detail: "Message copied to clipboard",
                      life: 5000,
                    });
                  },
                });
              },
              content_style:
                'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px }',
            },
            ...(isLightMode ? {} : { skin: "oxide-dark", content_css: "dark" }),
          }}
          onInit={(_, editor) => {
            editor.on("input", () => {
              // Only fires on user input
              const content = editor.getContent();
              setSaveState((x) => ({ ...x, dirty: true }));
              debouncedUpdate(content);
            });
          }}
          onEditorChange={(content) => {
            setText(content);
          }}
        />
      )}
    </Card>
  );
};
